import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import {toast} from 'react-toastify';

import {ChannelLogo} from '../../channels/ChannelLogo';
import {CURRENCY_CODES} from '../../elements/currencyCodes';
import {CaretDown, FolderSimple, MagnifyingGlass, Plus} from '../../icons';
import {Spinner} from '../../Spinner';
import {Switch} from '../../buttons/Swtich';
import {fetchAgencyData, fetchCandidates, fetchSuggestedBudgets, postAddBulk} from '../api';
import {normalizeForSearch} from '../proposalListView';
import {amountFor, BudgetAmount, formatMoney} from '../steps/BudgetAmount';
import {AccountChip} from '../steps/ClientProposalRow';
import {BucketGroup} from '../steps/ReviewBucket';
import {budgetForAccount, parseAddBulk422, soleCurrencyOf} from '../wizardPayload';

/**
 * Desde cuántas cuentas aparece el buscador.
 *
 * Mismo criterio que la barra del paso 3 (`TOOLBAR_MIN_ROWS`): sobre una lista corta el buscador es
 * ruido que hace parecer complicado algo que se resuelve mirando. El umbral es más bajo porque acá
 * las filas no se agrupan en clientes — cada cuenta es una fila — y el muro llega antes.
 */
export const SEARCH_MIN_ACCOUNTS = 15;

/**
 * Cuántas cuentas sin cliente lista a la vez «¿Falta una cuenta?».
 *
 * Hay agencias con cientos de cuentas sin dueño, y listarlas todas debajo del buscador era volver a
 * dibujar el muro que el rediseño sacó de la pantalla. Con el tope, la lista se afina escribiendo.
 * Ocho y no veinte: con veinte, medido en la captura, el buscador abierto pasaba el alto del modal y
 * empujaba el botón de crear fuera de la vista.
 */
export const FINDER_MAX_ROWS = 8;

// Dónde va cada cuenta del padrón (ver `placementOf`).
const MAIN = 'main';
// El paso 0 (`NewLinePathModal`) cuenta con la misma regla qué cuentas propias llegan a la lista
// principal: si contara distinto, ofrecería un camino que acá abre vacío.
export const PLACEMENT_MAIN = MAIN;
const DORMANT = 'dormant';
const CLOSED = 'closed';
const UNOWNED = 'unowned';
const COVERED = 'covered';

// El estado del presupuesto sugerido de cada fila: el GET se pide por cuenta, a medida que las filas
// aparecen, y una fila sin respuesta todavía no puede decir nada de su cuenta.
const BUDGET_READY = 'ready';
const BUDGET_FAILED = 'failed';

/**
 * «Desde tus cuentas conectadas»: una línea de seguimiento por cada cuenta elegida, con el
 * presupuesto del mes ya sugerido.
 *
 * Es el camino rápido del paso 0 y el mismo motor que usa el túnel de alta, en versión de una sola
 * pantalla, y trae lo que el túnel ya resolvía (card ArBAeihS): el camino es «revisá y confirmá», no
 * «armá la lista desde cero». Arriba, las cuentas del cliente que están andando, prendidas y con su
 * presupuesto sugerido. Todo lo demás existe, pero no compite con eso:
 *
 * - las dormidas y las cerradas en la plataforma van a un cajón plegado, con «Usar igual»;
 * - las que no tienen cliente se buscan a propósito («¿Falta una cuenta?»): elegir una la vincula;
 * - las que ya tienen su línea se resumen en un renglón.
 *
 * **Nada que no sea del cliente nace prendido.** Prender una cuenta sin dueño es VINCULARLA a la
 * ficha abierta, y nacían todas prendidas: en una agencia con cientos de cuentas sueltas un clic en
 * «Crear» las vinculaba a todas (el bug de ArBAeihS, `pathZeroInitialSelection.test.js`).
 *
 * ⚠️ **Buscar es una LENTE y nunca una decisión.** Lo que se va a crear sale siempre de la selección
 * completa (`chosen`), jamás de las filas visibles: con el filtro puesto, el botón sigue prometiendo
 * las N cuentas elegidas, incluidas las que en ese momento no están en pantalla. Y el botón se arma
 * con los MISMOS ítems que viajan en el POST (`items`): no puede prometer un monto que no se manda.
 *
 * `startWithFinderOpen`: el paso 0 lo pide cuando el cliente no tiene cuentas propias sin línea y la
 * agencia sí tiene cuentas sin cliente. La lista principal está vacía y lo único que se puede hacer
 * es buscar una: abrirlo cerrado era un click más para llegar a lo único que había.
 */
export function GlobalLinesFromAccountsModal({isOpen, toggle, clientId, clientName, agencyClients,
                                              candidates: given, startWithFinderOpen = false, onCreated}) {
    const {t, i18n} = useTranslation();
    const [candidates, setCandidates] = useState(null);
    const [selection, setSelection] = useState({});
    // Las que el usuario trajo a la lista principal desde el cajón o desde el buscador de las sin
    // cliente. Viven aparte de `selection`: apagar una traída no la devuelve a su lugar —una fila que
    // salta de sección al tocar su interruptor se pierde de vista—, queda en la lista, apagada.
    const [promoted, setPromoted] = useState({});
    const [assignment, setAssignment] = useState({});
    const [budgets, setBudgets] = useState({});
    const [budgetStatus, setBudgetStatus] = useState({});
    const [budgetEdits, setBudgetEdits] = useState({});
    const [canEditCurrency, setCanEditCurrency] = useState(false);
    // Las filas que el último intento de crear rechazó (`byPk` de la respuesta de `add-bulk`).
    const [rowErrors, setRowErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [failed, setFailed] = useState(false);
    // Qué está mirando el usuario. Es estado de VISTA: no toca `selection`, así que buscar no
    // desmarca nada ni cambia lo que el botón promete crear.
    const [query, setQuery] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [coveredOpen, setCoveredOpen] = useState(false);
    const [finderOpen, setFinderOpen] = useState(false);
    const [finderQuery, setFinderQuery] = useState('');
    const [finderShowAll, setFinderShowAll] = useState(false);
    // Qué cuentas ya salieron a pedir su presupuesto en esta apertura. Un ref y no estado: el doble
    // efecto del StrictMode, o cualquier render con las mismas filas, volvería a pedirlas.
    const requestedBudgets = useRef(new Set());
    // Cada apertura es una sesión: una respuesta de la anterior que llega tarde no se mezcla.
    const session = useRef(0);

    useEffect(() => {
        if (!isOpen) return;
        session.current += 1;
        requestedBudgets.current = new Set();
        setCandidates(null);
        setPromoted({});
        setBudgets({});
        setBudgetStatus({});
        setBudgetEdits({});
        setRowErrors({});
        // La lente se limpia al abrir: una búsqueda que sobrevive al cierre deja la lista corta
        // sin motivo visible y se lee como que faltan cuentas.
        setQuery('');
        setFinderQuery('');
        setFinderOpen(Boolean(startWithFinderOpen));
        setFinderShowAll(false);
        setDrawerOpen(false);
        setCoveredOpen(false);
        // Si quien abre este modal ya tenía las cuentas, se usan: pedirlas de nuevo trae la cartera
        // entera de la agencia por segunda vez.
        setFailed(false);
        const load = given ? Promise.resolve({status: 'OK', data: given}) : fetchCandidates();
        load.then(response => {
            if (response?.status !== 'OK') {
                // Una respuesta que no llegó NO es «no hay cuentas»: con la lista vacía el usuario
                // cree que no tiene nada que dar de alta y se va.
                setFailed(true);
                setCandidates({accounts: []});
                return;
            }
            const accounts = accountsFor(response.data.accounts, clientId);
            setCandidates({accounts});
            // Nacen prendidas SÓLO las de la lista principal sin haber tocado nada: las del cliente
            // que están andando. Una sin dueño, una dormida o una cerrada no nacen prendidas nunca.
            const initial = {};
            accounts.forEach(account => {
                if (placementOf(account, {}) === MAIN) initial[account.id] = true;
            });
            setSelection(initial);
        });
        // Sólo si la agencia puede elegir la moneda de una línea: si no, el selector no se ofrece
        // (el mismo GET y la misma regla que el paso 4 del túnel).
        fetchAgencyData().then(response => {
            setCanEditCurrency(response?.status === 'OK' && Boolean(response.general_data?.can_edit_currency));
        });
    }, [isOpen, clientId, given, startWithFinderOpen]);

    // El `|| []` va DENTRO del memo: como expresión suelta devuelve un array nuevo en cada render
    // mientras las cuentas no llegaron, y con eso todo lo de abajo se recalcularía siempre —
    // recorriendo el padrón entero de la agencia en cada tecla de cualquier otro estado.
    const allAccounts = useMemo(() => candidates?.accounts || [], [candidates]);
    const placed = useMemo(() => placeAll(allAccounts, promoted), [allAccounts, promoted]);
    const mainAccounts = placed[MAIN];
    const visibleAccounts = useMemo(() => accountsMatching(mainAccounts, query), [mainAccounts, query]);
    const groups = useMemo(() => groupByClient(visibleAccounts, t, {clientId, clientName}),
                           [visibleAccounts, t, clientId, clientName]);
    // El buscador se ofrece cuando la lista es un muro, y se mide sobre la lista COMPLETA: si
    // dependiera de lo visible, filtrar hasta doce filas haría desaparecer el control con el que se
    // filtró y no habría cómo volver.
    const showSearch = mainAccounts.length >= SEARCH_MIN_ACCOUNTS;

    // Cada fila de la lista principal pide su presupuesto sugerido, prendida o no: prenderla después
    // no puede dejarla esperando. Se pide por las que falten, así que traer una cuenta desde el
    // cajón o desde el buscador pide sólo esa.
    const mainKey = mainAccounts.map(account => account.id).join(',');
    useEffect(() => {
        if (!isOpen || !mainKey) return;
        const pks = mainKey.split(',').filter(pk => !requestedBudgets.current.has(pk));
        if (!pks.length) return;
        pks.forEach(pk => requestedBudgets.current.add(pk));
        const asked = session.current;
        // Sin `prepare-budgets` ni poll, a propósito (fede, 30/09): una cuenta sin catálogo todavía
        // dice «sin sugerencia todavía» y el monto se completa después. Las ya vinculadas a un
        // cliente suelen tenerlo, y hacer esperar a toda la pantalla por el caso raro no vale.
        fetchSuggestedBudgets(pks).then(response => {
            if (asked !== session.current) return;
            const got = response?.status === 'OK' ? (response.data?.budgets || {}) : {};
            setBudgets(previous => ({...previous, ...got}));
            // Una cuenta que no vino en la respuesta —su tanda se cayó, o se cayó todo— queda
            // marcada como falla y no como «sin gasto»: un error no se pinta como un dato.
            setBudgetStatus(previous => {
                const next = {...previous};
                pks.forEach(pk => { next[pk] = got[pk] ? BUDGET_READY : BUDGET_FAILED; });
                return next;
            });
        });
    }, [isOpen, mainKey]);

    const chosen = mainAccounts.filter(account => selection[account.id]);
    // Con un cliente en contexto no hay nada que elegir: se está creando desde SU ficha, así que
    // una cuenta sin dueño es de él. El selector sólo tiene sentido en el paso 0 general.
    const clientOf = account => (account.agency_client ? account.agency_client.id
                                                       : (clientId ? Number(clientId) : assignment[account.id]));
    const missingClient = chosen.filter(account => !clientOf(account));
    // Elegidas que el filtro dejó fuera de la vista. Sin este número, buscar «Rayovac» con doce
    // cuentas ya tildadas muestra una fila y un botón que dice «Crear 13 líneas».
    const visibleIds = new Set(visibleAccounts.map(account => account.id));
    const hiddenChosen = chosen.filter(account => !visibleIds.has(account.id)).length;

    // El presupuesto de cada fila, resuelto UNA vez: la celda del monto lo pinta, el botón lo suma y
    // el POST lo manda. La moneda de respaldo sale del padrón entero, como en el túnel: calcularla
    // sobre las elegidas haría que prender una fila cambie la moneda del presupuesto de OTRA.
    const fallbackCurrency = useMemo(() => soleCurrencyOf(candidates, budgets), [candidates, budgets]);
    const resolved = useMemo(() => {
        const byPk = {};
        mainAccounts.forEach(account => {
            const pk = String(account.id);
            byPk[pk] = resolvedBudgetOf({pk, budgets, budgetEdits, fallbackCurrency});
        });
        return byPk;
    }, [mainAccounts, budgets, budgetEdits, fallbackCurrency]);
    const items = chosen.map(account => {
        const item = {channel: account.channel, account_id: account.account_id,
                      agency_client_id: clientOf(account)};
        const {budget} = resolved[String(account.id)];
        if (budget) item.budget = budget;
        return item;
    });
    const totals = totalsByCurrencyOf(items);
    const linking = clientId ? chosen.filter(account => !account.agency_client).length : 0;
    // Con un presupuesto todavía en camino no se crea: `add-bulk` mandaría la línea sin monto y la
    // pantalla lo estaba por mostrar.
    const waitingBudgets = chosen.some(account => !isBudgetSettled(budgetStatus[String(account.id)]));

    // Por qué una cuenta que tenía un monto nace sin él, dicho en su fila.
    const noteOf = blocked => {
        if (!blocked) return null;
        return blocked.reason === CURRENCY_NOT_SUPPORTED
            ? t('path_zero.budget_currency_not_supported', {currency: blocked.currency,
                                                            interpolation: {escapeValue: false}})
            : t('path_zero.budget_currency_missing');
    };

    const create = () => {
        if (!chosen.length) return;
        setSaving(true);
        setRowErrors({});
        postAddBulk(items).then(response => {
            setSaving(false);
            if (response?.status === 'OK') {
                const created = response.data?.business_units_created_count || 0;
                toast.success(t('path_zero.created', {count: created}));
                toggle();
                if (onCreated) onCreated(response.data);
                return;
            }
            // Nunca el mensaje crudo del backend: puede nombrar cosas de otra agencia.
            const parsed = parseAddBulk422(response, chosen.map(account => String(account.id)));
            // La fila que el backend rechazó queda marcada: con el toast solo, «Revísalo» no tenía
            // nada que revisar. `add-bulk` valida por ítem ANTES de escribir, así que un ítem malo
            // frena a todos y hay que poder encontrarlo.
            setRowErrors(Object.fromEntries(Object.keys(parsed.byPk).map(pk => [pk, true])));
            const first = Object.values(parsed.byPk).flat()[0] || parsed.unassigned[0];
            toast.error(first ? t(`path_zero.error.${(first.code || 'generic').toLowerCase()}`,
                                  t('path_zero.error.generic'))
                              : t('path_zero.error.generic'));
        });
    };

    const promote = account => {
        setPromoted(previous => ({...previous, [account.id]: true}));
        setSelection(previous => ({...previous, [account.id]: true}));
    };

    // Tocar la fila que falló es atenderla: la marca se va y vuelve sólo si el próximo intento falla.
    const clearRowError = account => {
        setRowErrors(previous => {
            if (!previous[String(account.id)]) return previous;
            const next = {...previous};
            delete next[String(account.id)];
            return next;
        });
    };

    const editBudget = (account, amount, currency) => {
        setBudgetEdits(previous => ({...previous, [String(account.id)]: {amount, currency}}));
        clearRowError(account);
    };

    const nothingAtAll = allAccounts.length === 0;

    // El ancho lo da `-lg` y no `size="lg"`: `.modal-lg` de Bootstrap pierde contra el
    // `.modal-dialog { max-width: 720px }` de `_modals.scss` (misma especificidad, declarado
    // después). La regla que sí gana está en `pathZero.scss`.
    return (
        <Modal isOpen={isOpen} toggle={toggle} className="pz-modal -lg" backdrop="static">
            <ModalHeader toggle={toggle}>{t('path_zero.accounts_title')}</ModalHeader>
            <ModalBody>
                <div className="modal-section pz-accounts">
                    <p className="pz-lead">{t('path_zero.accounts_lead')}</p>

                    {!candidates && (
                        <div className="box-loader"><Spinner color="positive"/></div>
                    )}

                    {candidates && failed && (
                        <p className="pz-empty">{t('path_zero.accounts_failed')}</p>
                    )}

                    {candidates && !failed && showSearch && (
                        <div className="pz-search">
                            <MagnifyingGlass size={15} className="pz-search__icon"/>
                            <input type="search" value={query} className="pz-search__input"
                                   placeholder={t('path_zero.search_placeholder')}
                                   aria-label={t('path_zero.search_label')}
                                   onChange={event => setQuery(event.target.value)}/>
                        </div>
                    )}

                    {/* Sin cuentas de entrada y «tu búsqueda no encontró nada» son cosas distintas:
                        con el mismo texto, filtrar mal se lee como que no hay nada que dar de alta
                        y el usuario cierra la ventana. Y «no hay nada activo arriba» es una tercera:
                        lo que haya está abajo, en el cajón, en el buscador o ya con su línea. */}
                    {candidates && !failed && groups.length === 0 && (
                        <p className="pz-empty">
                            {query ? t('path_zero.accounts_no_matches', {count: mainAccounts.length})
                                : nothingAtAll ? t('path_zero.accounts_empty')
                                : t('path_zero.accounts_empty_main')}
                        </p>
                    )}

                    {groups.map(group => (
                        <section className="pz-group" key={group.key}>
                            <header className="pz-group__head">
                                <span className="pz-group__name">{group.name}</span>
                                {group.hint && <span className="pz-group__hint">{group.hint}</span>}
                            </header>
                            {group.accounts.map(account => {
                                const pk = String(account.id);
                                const on = Boolean(selection[account.id]);
                                const nameId = `pz-account-${account.id}`;
                                return (
                                    <div className={`pz-row -budget${on ? '' : ' -off'}${rowErrors[pk] ? ' -error' : ''}`}
                                         key={account.id}>
                                        {/* El `<label>` del `Switch` no tiene texto: sin el nombre de la
                                            cuenta, un lector de pantalla anunciaba «casilla, marcada» en el
                                            control que decide qué línea se crea. */}
                                        <Switch name={`account-${account.id}`} checked={on}
                                                aria-labelledby={nameId}
                                                onChange={event => {
                                                    setSelection({...selection, [account.id]: event.target.checked});
                                                    clearRowError(account);
                                                }}
                                                style={{margin: 0}}/>
                                        <span className="pz-row__account">
                                            <span className="pz-row__title">
                                                <span className="pz-row__logo">
                                                    <ChannelLogo channel={account.channel} hspace={0} vspace={0}
                                                                 marginLeft={0} padding={0}/>
                                                </span>
                                                <span className="pz-row__name" id={nameId}>
                                                    {account.name || account.account_id}
                                                </span>
                                            </span>
                                            {rowErrors[pk] && (
                                                <span className="pz-row__error" role="alert">
                                                    {t('path_zero.row_failed')}
                                                </span>
                                            )}
                                            {/* Una traída del cajón sigue siendo lo que era: se dice, para
                                                que no se lea como una cuenta más que está andando. */}
                                            {conditionOf(account) && (
                                                <span className="pz-row__flag">
                                                    {conditionOf(account) === CLOSED
                                                        ? (account.liveness_reason || t('path_zero.flag_closed'))
                                                        : t('path_zero.flag_dormant')}
                                                </span>
                                            )}
                                            {!account.agency_client && !clientId && (
                                                <select className="pz-row__select"
                                                        value={assignment[account.id] || ''}
                                                        onChange={event => setAssignment({
                                                            ...assignment, [account.id]: Number(event.target.value),
                                                        })}
                                                        aria-label={t('path_zero.pick_client')}>
                                                    <option value="">{t('path_zero.pick_client')}</option>
                                                    {(agencyClients || []).map(client => (
                                                        <option key={client.id} value={client.id}>{client.name}</option>
                                                    ))}
                                                </select>
                                            )}
                                        </span>
                                        <BudgetAmount suggestion={budgets[pk]} edit={budgetEdits[pk]}
                                                      onEdit={(amount, currency) => editBudget(account, amount, currency)}
                                                      preparing={false} catalogRequested={false}
                                                      canEditCurrency={canEditCurrency}
                                                      status={rowBudgetStatusOf(budgetStatus[pk])}
                                                      resolved={{budget: resolved[pk].budget,
                                                                 note: noteOf(resolved[pk].blocked)}}/>
                                    </div>
                                );
                            })}
                        </section>
                    ))}

                    {/* Con el filtro puesto, la lista NO es el padrón: decirlo es lo que evita que
                        el usuario cierre creyendo que vio todas sus cuentas. Y si hay elegidas
                        fuera de la vista, se nombran — el botón las va a crear igual. */}
                    {query && groups.length > 0 && (
                        <p className="pz-showing" role="status">
                            {t('path_zero.accounts_showing',
                               {shown: visibleAccounts.length, total: mainAccounts.length})}
                            {hiddenChosen > 0 && ` · ${t('path_zero.accounts_chosen_hidden', {count: hiddenChosen})}`}
                        </p>
                    )}

                    {candidates && !failed && (
                        <>
                            <SleepingDrawer dormant={placed[DORMANT]} closed={placed[CLOSED]}
                                            open={drawerOpen} onToggle={() => setDrawerOpen(!drawerOpen)}
                                            onUse={promote}/>
                            <UnownedFinder accounts={placed[UNOWNED]} open={finderOpen}
                                           onOpen={() => setFinderOpen(true)}
                                           onClose={() => setFinderOpen(false)}
                                           query={finderQuery} onQuery={setFinderQuery}
                                           showAll={finderShowAll} onShowAll={setFinderShowAll}
                                           clientId={clientId} clientName={clientName} onAdd={promote}/>
                            <CoveredSummary accounts={placed[COVERED]} open={coveredOpen}
                                            onToggle={() => setCoveredOpen(!coveredOpen)}/>
                        </>
                    )}

                    <p className="pz-note">{t('path_zero.accounts_note')}</p>
                </div>
            </ModalBody>
            <ModalFooter>
                <button className="btn -negative" onClick={toggle}>{t('path_zero.back')}</button>
                <button className="btn pz-create" onClick={create}
                        disabled={saving || waitingBudgets || chosen.length === 0 || missingClient.length > 0}>
                    {missingClient.length > 0 ? t('path_zero.pick_client_first', {count: missingClient.length}) : (
                        /* Lo que el botón dice es lo que el POST hace: cuántas líneas, con cuánto por mes
                           en total —una suma por moneda: ARS y USD no se suman— y a cuántas cuentas
                           vincula. Sale de `items`, que es exactamente lo que se manda. */
                        <>
                            <span>{t('path_zero.create', {count: chosen.length})}</span>
                            {/* Cada pieza en su span y el espacio entre ellas lo da el `gap` del botón:
                                así, si no entra en un renglón, corta ENTRE piezas y no en el medio de
                                un monto. */}
                            {totals.length > 0 && (
                                <span className="pz-create__total">
                                    {'· '}
                                    {totals.map(each => formatMoney(each.amount, each.currency, i18n.language))
                                           .join(' + ')}
                                    {' '}{t('onboarding.budgets.per_month')}
                                </span>
                            )}
                            {linking > 0 && (
                                <span className="pz-create__link">
                                    {clientName
                                        ? t('path_zero.and_link', {count: linking, client: clientName,
                                                                   interpolation: {escapeValue: false}})
                                        : t('path_zero.and_link_this_client', {count: linking})}
                                </span>
                            )}
                        </>
                    )}
                </button>
            </ModalFooter>
        </Modal>
    );
}

/**
 * Las cuentas del cliente que no están andando: sin actividad reciente o cerradas en la plataforma.
 *
 * Plegado y debajo de la lista, como el cajón del paso 3 del túnel (`ReviewBucket`): son la minoría
 * y no pueden competir con lo que se va a crear. No nacen prendidas porque una línea de seguimiento
 * vigila un presupuesto que se está gastando; «Usar igual» las sube a la lista, prendidas.
 */
function SleepingDrawer({dormant, closed, open, onToggle, onUse}) {
    const {t} = useTranslation();
    const head = useRef(null);
    const count = dormant.length + closed.length;
    if (!count) return null;

    // «Usar igual» saca la fila del cajón y desmonta el botón que tenía el foco: sin esto caía en
    // `body` y quien usa el teclado volvía al principio del modal (review C-modal-lineas-04).
    const use = account => {
        onUse(account);
        if (head.current) head.current.focus();
    };
    // El nombre de la cuenta va en el nombre accesible: con N «Usar igual» iguales, un lector de
    // pantalla no decía cuál (C-modal-lineas-03).
    // No `useLabel`: con el prefijo `use`, `react-hooks/rules-of-hooks` la toma por un hook y marca
    // error cada llamado dentro del `map`.
    const labelOfUse = account => t('path_zero.use_anyway_aria', {name: nameOf(account),
                                                               interpolation: {escapeValue: false}});

    return (
        <section className={`ob-bucket pz-drawer${open ? ' -open' : ''}`}>
            <button type="button" className="ob-bucket__head" onClick={onToggle} aria-expanded={open} ref={head}>
                <FolderSimple size={16}/>
                <span className="ob-bucket__title">{t('path_zero.drawer_title', {count})}</span>
                <span className="ob-bucket__hint">{t('path_zero.drawer_hint')}</span>
                <CaretDown size={14} className="ob-bucket__caret"/>
            </button>

            {open && (
                <div className="ob-bucket__body">
                    {dormant.length > 0 && (
                        <BucketGroup ns="path_zero" titleKey="drawer_dormant" hintKey="drawer_dormant_hint"
                                     count={dormant.length}>
                            {dormant.map(account => (
                                <div className="ob-bucket__row -no-reason" key={account.id}>
                                    <AccountChip account={account}/>
                                    <div className="ob-bucket__actions">
                                        <button type="button" className="ob-link" onClick={() => use(account)}
                                                aria-label={labelOfUse(account)}>
                                            {t('path_zero.use_anyway')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </BucketGroup>
                    )}

                    {/* El motivo va por fila: varía (cancelada, suspendida, archivada) y es lo que
                        le dice al usuario si la va a poder reactivar. Es el texto que la plataforma
                        reportó, el mismo que la agencia ve en Conexiones. */}
                    {closed.length > 0 && (
                        <BucketGroup ns="path_zero" titleKey="drawer_closed" hintKey="drawer_closed_hint"
                                     count={closed.length}>
                            {closed.map(account => (
                                <div className="ob-bucket__row" key={account.id}>
                                    <AccountChip account={account}/>
                                    <span className="ob-bucket__reason">
                                        {account.liveness_reason || t('path_zero.flag_closed')}
                                    </span>
                                    <div className="ob-bucket__actions">
                                        <button type="button" className="ob-link" onClick={() => use(account)}
                                                aria-label={labelOfUse(account)}>
                                            {t('path_zero.use_anyway')}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </BucketGroup>
                    )}
                </div>
            )}
        </section>
    );
}

/**
 * «¿Falta una cuenta?»: las cuentas de la agencia que no tienen cliente, detrás de un buscador.
 *
 * Estaban en la lista principal, todas prendidas, y eran el muro de la captura de ArBAeihS: cientos
 * de filas tapando las pocas que el botón iba a crear. Ahora se buscan a propósito y no hay nada
 * prendido: «Agregar» la sube a la lista y, al crear, la vincula al cliente. Por default muestra las
 * que están andando; las dormidas y cerradas se suman a pedido.
 */
function UnownedFinder({accounts, open, onOpen, onClose, query, onQuery, showAll, onShowAll, clientId,
                        clientName, onAdd}) {
    const {t} = useTranslation();
    const input = useRef(null);
    const opener = useRef(null);
    const returnFocus = useRef(false);
    // «Cerrar» desmonta el buscador entero, con el foco adentro: vuelve al botón que lo abrió, que
    // recién existe después del render (C-modal-lineas-04).
    useEffect(() => {
        if (open || !returnFocus.current) return;
        returnFocus.current = false;
        if (opener.current) opener.current.focus();
    }, [open]);
    const inactive = accounts.filter(account => conditionOf(account)).length;
    const pool = showAll ? accounts : accounts.filter(account => !conditionOf(account));
    const matching = accountsMatching(pool, query);
    if (!accounts.length) return null;

    // «Agregar» sube la cuenta a la lista y su fila sale del buscador —con el botón enfocado—: el foco
    // vuelve al buscador, que es donde se sigue.
    const add = account => {
        onAdd(account);
        if (input.current) input.current.focus();
    };
    const close = () => {
        returnFocus.current = true;
        onClose();
    };

    if (!open) {
        return (
            <button type="button" className="ob-link pz-finder__open" onClick={onOpen} ref={opener}>
                <MagnifyingGlass size={14}/>
                {' '}{t('path_zero.finder_open', {count: accounts.length})}
            </button>
        );
    }

    const shown = matching.slice(0, FINDER_MAX_ROWS);
    return (
        <section className="pz-finder">
            <header className="pz-finder__head">
                <span className="pz-finder__title">{t('path_zero.finder_title')}</span>
                <span className="pz-finder__hint">
                    {clientName ? t('path_zero.finder_hint_for_client', {client: clientName,
                                                                          interpolation: {escapeValue: false}})
                        : clientId ? t('path_zero.finder_hint_this_client')
                        : t('path_zero.finder_hint')}
                </span>
            </header>
            <div className="pz-search">
                <MagnifyingGlass size={15} className="pz-search__icon"/>
                {/* El foco va al buscador: abrir esto ES decir «quiero buscar una». */}
                <input type="search" value={query} className="pz-search__input" autoFocus ref={input}
                       placeholder={t('path_zero.finder_placeholder')}
                       aria-label={t('path_zero.finder_label')}
                       onChange={event => onQuery(event.target.value)}/>
            </div>

            {/* «No coincide nada» y «no hay ninguna andando» son respuestas distintas: la segunda
                se arregla con el botón de abajo, no escribiendo otra cosa. */}
            {shown.length === 0 ? (
                <p className="pz-empty">
                    {pool.length ? t('path_zero.finder_no_matches', {count: pool.length})
                                 : t('path_zero.finder_none_active')}
                </p>
            ) : (
                <div className="pz-finder__list">
                    {shown.map(account => (
                        <div className="pz-finder__row" key={account.id}>
                            <AccountChip account={account}/>
                            <span className="pz-finder__id">{account.account_id}</span>
                            <span className="pz-finder__flag">
                                {conditionOf(account) === CLOSED
                                    ? (account.liveness_reason || t('path_zero.flag_closed'))
                                    : conditionOf(account) === DORMANT ? t('path_zero.flag_dormant') : ''}
                            </span>
                            <button type="button" className="ob-btn -mini -ghost" onClick={() => add(account)}
                                    aria-label={t('path_zero.finder_add_aria', {name: nameOf(account),
                                                                                interpolation: {escapeValue: false}})}>
                                <Plus size={12}/> {t('path_zero.finder_add')}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            <p className="pz-finder__foot">
                {matching.length > shown.length && (
                    <span>{t('path_zero.finder_more', {count: matching.length - shown.length})}</span>
                )}
                {/* Las dormidas y cerradas no se esconden sin decirlo: el contador es lo que evita
                    que el usuario concluya que la cuenta que busca no está conectada. */}
                {inactive > 0 && (
                    <button type="button" className="ob-link" onClick={() => onShowAll(!showAll)}>
                        {showAll ? t('path_zero.finder_only_active')
                                 : t('path_zero.finder_show_inactive', {count: inactive})}
                    </button>
                )}
                {/* Abierto ocupa lugar: cerrarlo devuelve el botón de crear a la vista. Lo agregado
                    se queda arriba, en la lista. */}
                <button type="button" className="ob-link -muted pz-finder__close" onClick={close}>
                    {t('path_zero.finder_close')}
                </button>
            </p>
        </section>
    );
}

/**
 * Las que ya tienen su línea, en un renglón.
 *
 * Se siguen mostrando —una cuenta que falta sin explicación se lee como un dato perdido—, pero ya no
 * una por una al final: no son accionables, y eran la mitad de la lista en un cliente con historia.
 */
function CoveredSummary({accounts, open, onToggle}) {
    const {t} = useTranslation();
    if (!accounts.length) return null;

    return (
        <section className={`pz-covered${open ? ' -open' : ''}`}>
            <button type="button" className="pz-covered__head" onClick={onToggle} aria-expanded={open}>
                <span>{t('path_zero.covered_summary', {count: accounts.length})}</span>
                <CaretDown size={13} className="pz-covered__caret"/>
            </button>
            {open && (
                <div className="pz-covered__list">
                    {accounts.map(account => (
                        <div className="pz-row -covered" key={account.id}>
                            <span className="pz-row__logo">
                                <ChannelLogo channel={account.channel} hspace={0} vspace={0} marginLeft={0} padding={0}/>
                            </span>
                            <span className="pz-row__name">{account.name || account.account_id}</span>
                            <span className="pz-row__tag">
                                {t('path_zero.already_covered', {line: account.covering_business_unit?.name,
                                                                 interpolation: {escapeValue: false}})}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

/**
 * Las cuentas que responden a lo que el usuario escribió.
 *
 * Busca por el nombre de la cuenta, su id de plataforma y el nombre del cliente al que ya está
 * asignada — las tres formas en que una agencia nombra la misma cuenta. Comparte `normalizeForSearch`
 * con el paso 3 para que «ferreteria» encuentre «Ferretería Central» acá también.
 */
export function accountsMatching(accounts, query) {
    const needle = normalizeForSearch(query);
    if (!needle) return accounts;

    return accounts.filter(account =>
        normalizeForSearch(account.name).includes(needle)
        || normalizeForSearch(account.account_id).includes(needle)
        || normalizeForSearch(account.agency_client?.name).includes(needle));
}

/**
 * Dónde va una cuenta: la lista principal, el cajón (dormida o cerrada), el buscador de las sin
 * cliente o el renglón de las que ya tienen línea.
 *
 * Una traída a mano (`promoted`) va a la lista principal venga de donde venga. Cerrada le gana a
 * dormida: una cuenta que la plataforma dio de baja tampoco gasta, y el motivo que importa es el
 * primero. `activity === 'UNKNOWN'` NO es dormida — es que todavía no sincronizamos su catálogo, y
 * una cuenta recién conectada va con las activas.
 */
export function placementOf(account, promoted) {
    if (account.covered) return COVERED;
    if (promoted[account.id]) return MAIN;
    if (!account.agency_client) return UNOWNED;
    return conditionOf(account) || MAIN;
}

/** Cómo la llama la fila: su nombre, o el id de plataforma si la cuenta no tiene nombre. */
function nameOf(account) {
    return account.name || String(account.account_id);
}

/** `CLOSED`, `DORMANT` o `null` si la cuenta está andando. */
function conditionOf(account) {
    if (account.liveness === 'DEAD') return CLOSED;
    if (account.activity === 'DORMANT') return DORMANT;
    return null;
}

function placeAll(accounts, promoted) {
    const placed = {[MAIN]: [], [DORMANT]: [], [CLOSED]: [], [UNOWNED]: [], [COVERED]: []};
    accounts.forEach(account => placed[placementOf(account, promoted)].push(account));
    return placed;
}

/** Con un cliente en contexto se muestran sólo SUS cuentas y las que todavía no tienen dueño. */
function accountsFor(accounts, clientId) {
    if (!clientId) return accounts;
    return accounts.filter(account => !account.agency_client || account.agency_client.id === Number(clientId));
}

function isBudgetSettled(status) {
    return status === BUDGET_READY || status === BUDGET_FAILED;
}

/** Lo que la fila tiene que decir de su presupuesto. Sin respuesta todavía, está cargando. */
function rowBudgetStatusOf(status) {
    if (status === BUDGET_READY) return null;
    return status === BUDGET_FAILED ? 'failed' : 'loading';
}

// Por qué una cuenta que tenía un monto nace sin él (ver `resolvedBudgetOf`).
const CURRENCY_NOT_SUPPORTED = 'currency_not_supported';
const CURRENCY_MISSING = 'currency_missing';

/**
 * El presupuesto que VIAJA para una cuenta: `{budget: {amount, currency} | null, blocked}`.
 *
 * Es la única resolución: la fila la pinta, el botón la suma y el POST la manda. Parte de
 * `budgetForAccount` (el túnel: edición o sugerencia, y la moneda de respaldo para la cuenta que no
 * declara la suya) y le suma lo que el túnel no mira: que el backend ADMITA esa moneda. `add-bulk`
 * valida cada ítem antes de escribir, así que una sola cuenta en CAD rechazaba el pedido entero y
 * no se creaba ninguna línea (en prod hay 209 cuentas con monedas fuera de la lista, review
 * C-modal-lineas-01). Ahora esa cuenta nace sin monto y el resto se crea.
 *
 * `blocked` dice por qué una cuenta que TENÍA un monto nace sin él: una moneda que no se admite, o
 * ninguna moneda y sin respaldo (C-modal-lineas-02). Sin monto de entrada no hay nada que explicar.
 */
export function resolvedBudgetOf({pk, budgets, budgetEdits, fallbackCurrency}) {
    const budget = budgetForAccount({pk, budgets, budgetEdits, fallbackCurrency});
    if (budget && CURRENCY_CODES.includes(budget.currency)) return {budget, blocked: null};
    if (budget) return {budget: null, blocked: {reason: CURRENCY_NOT_SUPPORTED, currency: budget.currency}};
    const wanted = Number(amountFor(budgets?.[pk], budgetEdits?.[pk]));
    if (Number.isFinite(wanted) && wanted > 0) return {budget: null, blocked: {reason: CURRENCY_MISSING}};
    return {budget: null, blocked: null};
}

/**
 * El total por mes de lo que se va a crear, UNO POR MONEDA.
 *
 * Suma los `budget` de los ítems del POST, no lo que se ve: una fila sin monto no suma, y una cuenta
 * en ARS y otra en USD no tienen un total común — sumarlas produce un número que no existe.
 */
export function totalsByCurrencyOf(items) {
    const byCurrency = new Map();
    items.forEach(item => {
        if (!item.budget) return;
        const {amount, currency} = item.budget;
        byCurrency.set(currency, (byCurrency.get(currency) || 0) + amount);
    });
    return Array.from(byCurrency, ([currency, amount]) => ({currency, amount}));
}

function groupByClient(accounts, t, {clientId, clientName} = {}) {
    // En la ficha de un cliente no hay selector: la cuenta sin dueño que se agrega se vincula a ÉL,
    // y su grupo lo dice. Sin cliente en contexto, cada una elige el suyo.
    const unassignedName = !clientId ? t('path_zero.group_unassigned')
        : clientName ? t('path_zero.group_to_link', {client: clientName, interpolation: {escapeValue: false}})
        : t('path_zero.group_to_link_this_client');
    const unassignedHint = !clientId ? t('path_zero.group_unassigned_hint') : null;
    const groups = new Map();
    accounts.forEach(account => {
        const key = account.agency_client ? `client:${account.agency_client.id}` : 'unassigned';
        if (!groups.has(key)) {
            groups.set(key, {
                key,
                name: account.agency_client ? account.agency_client.name : unassignedName,
                hint: account.agency_client ? null : unassignedHint,
                accounts: [],
            });
        }
        groups.get(key).accounts.push(account);
    });

    // Sin cliente en contexto, las sin dueño van primero porque son las que piden una decisión (su
    // cliente). En la ficha de un cliente van DESPUÉS de las suyas: son las agregadas a mano.
    const unassignedFirst = !clientId;
    return Array.from(groups.values()).sort((a, b) => {
        if (a.key === 'unassigned') return unassignedFirst ? -1 : 1;
        if (b.key === 'unassigned') return unassignedFirst ? 1 : -1;
        return a.name.localeCompare(b.name);
    });
}
